import { useEffect, useRef, useState } from 'react';

/**
 * Holds `active` true for at least `ms` after it first becomes true.
 *
 * The announcements request usually resolves in a couple of hundred
 * milliseconds against a warm backend, which is long enough to mount a loader
 * and short enough that it reads as a flicker rather than a state. Anything
 * under ~200ms looks like a glitch, so once the branded loader is on screen it
 * stays long enough to be seen.
 *
 * The cost is that a fast load waits out the remainder of the window. That is
 * a deliberate trade for a once-per-session moment; pass 0 to disable.
 */
export default function useMinimumDuration(active, ms = 600) {
  const [held, setHeld] = useState(active);
  const startedAt = useRef(active ? Date.now() : null);

  useEffect(() => {
    if (active) {
      if (startedAt.current === null) startedAt.current = Date.now();
      setHeld(true);
      return undefined;
    }

    if (startedAt.current === null) {
      setHeld(false);
      return undefined;
    }

    const remaining = Math.max(ms - (Date.now() - startedAt.current), 0);
    if (remaining === 0) {
      setHeld(false);
      return undefined;
    }

    const timer = setTimeout(() => setHeld(false), remaining);
    return () => clearTimeout(timer);
  }, [active, ms]);

  return held;
}
